import { useNuxtApp } from '#app'
import type Konva from 'konva'
import type { Layer } from 'konva/lib/Layer'
import { Rect } from 'konva/lib/shapes/Rect'
import { defineStore } from 'pinia'
import { DEFAULT_GRID_HEIGHT, DEFAULT_GRID_WIDTH, GRID_START_OFFSET, SELECTABLE_TOOLS } from '~/composables/grid/constants'
import { calculatePreviewPixelRatio, SAVED_DESIGN_PREVIEW_MAX_AXIS } from '~/composables/previewImageSizing'
import { SweetAlertIcon } from '~/types/sweetalert'
import { useGridStore } from '~/stores/grid'
import { useLoadingStore } from '~/stores/loading'
import { useUserStore } from '~/stores/user'
import type { ShapeInterface } from '~/types/grid.types'
import { performAddToCart, type AddToCartOptions, type ProjectStoreActionContext } from '~/composables/addToCartAction'

export const useProjectStore = defineStore('project', {
  state: () => ({
    designName: null as string | null,
    designId: null as string | null,
    bgColor: null as string | null,
    saving: false,
    savingInProcess: false,
    saveTimeout: null as NodeJS.Timeout | null,
    gridRef: null as any,
    // Add cart-related state
    canFeature: false,
    username: null as string | null, // Username can be different per project
    isCommercialProject: false,
    projectName: null as string | null,
    salesperson: null as string | null,
    isAddingToCart: false,
  }),
  actions: {
    setDesignId(id: string | null) {
      this.designId = id
      const gridStore = useGridStore()
      gridStore.setLegendScopeId(id)
    },
    setGridRef(ref: any) {
      this.gridRef = ref
    },
    setDesignName(name: string) {
      this.designName = name
    },
    setDetails(id: string, name: string | null, color: string, canFeature = false, commercial = false, projectName = null, salesperson = null, username = null, email = null) {
      this.setDesignId(id)
      this.designName = name
      this.bgColor = color
      this.canFeature = canFeature
      this.isCommercialProject = commercial
      this.projectName = projectName
      this.salesperson = salesperson
      this.username = username
      if (email) {
        useGridStore().updateAuthorEmail(email)
      }
    },

    updateDetails(details: {
      id?: string,
      designName?: string,
      color?: string,
      canFeature?: boolean,
      commercial?: boolean,
      projectName?: string | null,
      salesperson?: string | null,
      username?: string | null
    }) {
      if (details.id !== undefined) this.setDesignId(details.id)
      if (details.designName !== undefined) this.designName = details.designName
      if (details.color !== undefined) this.bgColor = details.color
      if (details.canFeature !== undefined) this.canFeature = details.canFeature
      if (details.commercial !== undefined) this.isCommercialProject = details.commercial
      if (details.projectName !== undefined) this.projectName = details.projectName
      if (details.salesperson !== undefined) this.salesperson = details.salesperson
      if (details.username !== undefined) this.username = details.username
    },

    async load(designID: string, updateURL = true, importLink = false) {
      if (!designID) return

      const endpoint = importLink ? 'import' : 'designs'
      let designName;
      const design = await GET(`/api/${endpoint}/${designID}`);
      if (!design) throw new Error("Design not found");

      if (importLink) {
        designName = null
        useGridStore().updateSelectedTool(SELECTABLE_TOOLS.MOVE_GRID);
      }
      else {
        designName = design.design_name
      }

      this.setDetails(
        design.id,
        designName,
        design.grid_properties.backgroundColor,
        design.can_feature,
        design.commercial_project,
        design.project_name,
        design.salesperson,
        design.author_name,
        design.author_email
      )

      if (updateURL) {
        const currentQuery = useRoute().query
        useRouter().replace({ query: { id: design.id, ...(currentQuery.ar ? { ar: currentQuery.ar } : {}) } })
        // Update the cookie when loading a design
        setCookie('studio_design_id', design.id);
      }

      return design
    },

    async loadPreview(previewID: string) {
      if (!previewID) return

      const design = await GET(`/api/designs/preview/${previewID}`)
      if (!design) throw new Error("Design Preview not found")

      this.setDetails(
        design.id,
        design.design_name,
        design.grid_properties.backgroundColor,
        design.can_feature,
        design.commercial_project,
        design.project_name,
        design.salesperson,
        design.author_name,
        design.author_email
      )

      return design
    },

    async save(updateURL = true, skipToast = false) {
      const response = await this.saveDesign({ skipToast }) as { success: boolean; design: any }
      if (response.success) {
        this.setDetails(
          response.design.id,
          response.design.design_name,
          response.design.grid_properties.backgroundColor,
          response.design.can_feature,
          response.design.commercial_project,
          response.design.project_name,
          response.design.salesperson,
          response.design.author_name
        )

        if (updateURL) {
          useRouter().replace({ query: { id: response.design.id } })
          // Also update the cookie to track the latest used design
          setCookie('studio_design_id', response.design.id);
        }
      }
    },

    async saveDesign({
      nameRequired = false,
      loginRequired = false,
      saveAs = false,
      showIndicator = false,
      skipToast = false,
      confirm = false,
      keepLoading = false
    } = {}) {
      const { POST, PUT } = await import('~/composables/fetchingTools')
      const gridStore = useGridStore()
      const userStore = useUserStore()
      const loadingStore = useLoadingStore()
      const { $toast } = useNuxtApp()
      const currentId = this.designId

      // Initiate the image preview early without waiting for it to complete
      const imagePreviewPromise = this.getCanvasImage()

      this.saving = true
      if (showIndicator) {
        loadingStore.setLoading(true)
      }

      // Clear any existing save timeout
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout)
      }
      if (!this.username) {
        this.username = userStore.email
      }
      return new Promise((resolve) => {
        this.saveTimeout = setTimeout(async () => {
          try {
            const designData = this.buildDesignUpdate()
            const userEmail = getCookie(useConstants().FELT_RIGHT_USER_COOKIE);

            const designEmail = gridStore.authorEmail;
            const shouldCreateNew = (designEmail && designEmail !== "") &&
              (!userEmail || userStore.email === "" || designEmail !== userStore.email);

            // Wait for imagePreview only when needed (just before sending)
            const imagePreview = await imagePreviewPromise;

            const VERB = (!currentId || shouldCreateNew) ? POST : PUT
            const endpoint = (!currentId || shouldCreateNew)
              ? '/api/designs'
              : `/api/designs/${currentId}`
            const response = await VERB(endpoint, { ...designData, image: imagePreview });

            if (designEmail !== userStore.email) {
              designData.author_name = null;
              gridStore.updateAuthorEmail(userStore.email);
            }

            if (response?.success && response.design?.id) {
              // Only update URL if the ID has changed
              const urlParams = new URLSearchParams(window.location.search);
              const currentUrlId = urlParams.get('id');
              if (!currentUrlId || currentUrlId !== response.design.id) {
                useRouter().replace({ query: { id: response.design.id } })
              }
              // Update the cookie with the new design ID to track the latest used design
              setCookie('studio_design_id', response.design.id);
              // If this is a new design, add it to the user's designIds
              if (!currentId || shouldCreateNew) {
                userStore.designIds.push(response.design.id)
              }
            }

            this.setDesignId(response.design.id)
            this.savingInProcess = false
            this.saving = false
            resolve(response);
            if (!skipToast) {
              $toast.fire({
                text: 'Saved!',
                icon: SweetAlertIcon.SUCCESS,
              })
            }
          } catch (error) {
            console.error('Error saving design:', error)
            this.saving = false
            resolve({ success: false, error })
          } finally {
            if (showIndicator && !keepLoading) {
              loadingStore.setLoading(false)
            }
          }
        }, 800) // Same debounce time as original
      })
    },
    async getCanvasImage() {
      // Access the konva instance and clone it
      const konvaStage: Konva.Node = this.gridRef.getNode ? this.gridRef.getNode() : this.gridRef;
      const konvaClone: Konva.Node = konvaStage.clone();

      const stage = konvaClone.getStage();
      let width = DEFAULT_GRID_WIDTH;
      let height = DEFAULT_GRID_HEIGHT;
      stage?.scale({ x: 1, y: 1 }).position({ x: 0, y: 0 });
      // Removing layers that are not needed for the image
      const layersToDestroy = ['temp-item-layer', 'lasso-select-layer', 'rect-select-layer', 'selection-layer', 'resize-indicators-layer']
      const layers: Layer[] = stage?.getLayers() || [];
      layers.forEach((layer: any) => {
        if (layersToDestroy.includes(layer.name())) {
          layer.destroy();
        }
        else {
          layer.position({ x: 0, y: 0 });
        }
      });
      const gridLayer: Layer | undefined = stage?.findOne<Layer>('.gridLayer');
      if (gridLayer) {
        const thumbnailConfig = gridLayer.getAttr('thumbnailConfig')
        const bgNode = new Rect({
          x: 0,
          y: 0,
          width: thumbnailConfig.width,
          height: thumbnailConfig.height,
          fill: thumbnailConfig.bgColor,
        })
        // Remove all children (ruler, grid lines, etc)
        gridLayer.removeChildren();
        gridLayer.add(bgNode)
        width = thumbnailConfig.width
        height = thumbnailConfig.height
      }
      // Check if any shape is selected
      const shapesLayer = stage?.findOne<Layer>('.shapes-layer');
      if (shapesLayer) {
        shapesLayer.getChildren().forEach((shape: Konva.Node) => {
          shape.setAttrs({
            dash: undefined,
            dashOffset: undefined,
            strokeWidth: undefined,
            stroke: undefined,
          })
        });
      }
      // Converting the stage to an image — scale down to keep preview under Shopify's limit
      const pixelRatio = calculatePreviewPixelRatio(width, height, {
        maxAxis: SAVED_DESIGN_PREVIEW_MAX_AXIS,
      })
      const k2DataURL = stage?.toDataURL({
        pixelRatio,
        width,
        height,
        x: GRID_START_OFFSET,
        y: GRID_START_OFFSET
      });

      // Destroying the clone to free memory
      konvaClone.destroy();

      return k2DataURL;
    },
    async saveAs(designData = {}) {
      const { POST } = await import('~/composables/fetchingTools')
      const userStore = useUserStore()

      try {

        const saveData = {
          ...this.buildDesignUpdate(),
          ...designData
        }

        // Always use POST for saveAs to create new design
        const response = await POST('/api/designs', saveData)

        if (response?.success && response.design?.id) {
          userStore.designIds.push(response.design.id)
          // Update the cookie with the new design ID from saveAs
          setCookie('studio_design_id', response.design.id);
        }

        this.savingInProcess = false
        this.saving = false
        return response;
      } catch (error) {
        console.error('Error in saveAs:', error)
        return { success: false, error }
      }
    },

    reset() {
      this.setDesignId(null)
      this.designName = 'New Design'
      this.bgColor = null
    },

    async addToCart(options: AddToCartOptions = {}) {
      return performAddToCart(this as unknown as ProjectStoreActionContext, options)
    },

    setCartDetails(details: {
      isCommercialProject?: boolean,
      projectName?: string,
      salesperson?: string,
      canFeature?: boolean
    }) {
      if (details.isCommercialProject !== undefined) this.isCommercialProject = details.isCommercialProject
      if (details.projectName !== undefined) this.projectName = details.projectName
      if (details.salesperson !== undefined) this.salesperson = details.salesperson
      if (details.canFeature !== undefined) this.canFeature = details.canFeature
    },

    resetCartDetails() {
      this.isCommercialProject = false
      this.projectName = ''
      this.salesperson = ''
      this.canFeature = false
    },
    buildDesignUpdate() {
      const gridStore = useGridStore()
      const userStore = useUserStore()
      const onlyUniqueShapes = gridStore.shapeList.filter(
        (shape: ShapeInterface, index: number, self: Array<ShapeInterface>) =>
          index === self.findIndex((s) => s.id === shape.id)
      )
      return {
        grid: {
          gridPattern: gridStore.gridPattern?.id,
          gridWidth: gridStore.gridWidth,
          gridHeight: gridStore.gridHeight,
          gridBgColor: gridStore.gridBgColor,
          startOffset: gridStore.startOffset,
          shapeList: onlyUniqueShapes,
        },
        designWidth: gridStore.designWidth,
        designHeight: gridStore.designHeight,
        design_name: this.designName,
        author_name: this.username,
        author_email: userStore.email || "",
        can_feature: this.canFeature,
        commercial_project: this.isCommercialProject,
        project_name: this.projectName,
        salesperson: this.salesperson,
        tile_area: parseFloat(gridStore.designArea.split('ft')[0]),
      }
    }
  },
})
